import React from 'react';
import PropTypes from 'prop-types';
import RouterLink from 'react-router/lib/Link';
import suitcss from '../../../helpers/suitcss';
import { capitalize } from '../../../helpers/str';

import Headline from '../../basics/text/TextHeadline';
import SvgLoader from '../../basics/media/MediaSvgLoader';
import MediaImage from '../../basics/media/MediaImage';
import imageSizes from '../../../config/imageSizes';
import Copy from '../../basics/text/TextCopy';
import Callout from '../../../containers/callout/Callout';

import { imageShape } from '../../../propTypes/media';
import LazyShowElement from '../../basics/element/LazyShowElement';

function ContentGroupItem(props) {
  const {
    headline,
    copy,
    image,
    icon,
    align,
    imageAlign,
    imageWidth,
    link,
    inverted,
    moduleId,
    primaryModule,
    columns,
    withTextShadows,
  } = props;
  const modifiers = [
    align && `align${capitalize(align)}`,
    (image || icon) && imageWidth && `imageWidth${imageWidth}`,
    (image || icon) && imageAlign && `imageAlign${capitalize(imageAlign)}`,
    withTextShadows && 'textShadows',
    inverted && 'inverted',
  ];

  const { contentGroupItem: sizes } = imageSizes;
  // 100% images which are aligned left or right get max 50% width
  const includesText = Boolean(headline || copy);
  const width = (includesText && imageWidth === 100 && ['left', 'right'].includes(imageAlign)) ? 50 : imageWidth;
  const contentImage = {
    ...image,
    sizes: sizes[`cols-${columns}-${width}`],
  };

  return (
    <LazyShowElement
      className={suitcss({
        componentName: 'ContentGroupItem',
        modifiers,
      })}
    >
      {(headline || copy) &&
        <div
          className={suitcss({
            componentName: 'ContentGroupItem',
            descendantName: 'content',
          })}
        >
          {headline &&
            <div
              className={suitcss({
                componentName: 'ContentGroupItem',
                descendantName: 'headline',
              })}
            >
              <Headline element={primaryModule ? 'h1' : 'h2'} size="l" embedded>
                {headline}
              </Headline>
            </div>
          }
          {copy &&
            <div
              className={suitcss({
                componentName: 'ContentGroupItem',
                descendantName: 'copy',
              }, this)}
            >
              <Copy raw embedded inverted={inverted}>{copy}</Copy>
            </div>
          }
        </div>
      }
      {icon &&
        <div
          className={suitcss({
            componentName: 'ContentGroupItem',
            descendantName: 'image',
          }, this)}
        >
          <SvgLoader path={icon} inverted={inverted} />
        </div>
      }
      {image && !icon &&
        <div
          className={suitcss({
            componentName: 'ContentGroupItem',
            descendantName: 'image',
          }, this)}
        >
          {link &&
            <RouterLink
              to={link.url}
              target={link.external ? '_blank' : null}
              rel={link.external ? 'noopener noreferrer' : null}
            >
              <MediaImage {...contentImage} isLazy />
            </RouterLink>
          }
          {!link &&
            <MediaImage {...contentImage} isLazy />
          }
        </div>
      }
      <Callout
        theme="badge"
        targets={[moduleId]}
        inverted={inverted}
      />
    </LazyShowElement>
  );
}

ContentGroupItem.propTypes = {
  headline: PropTypes.string,
  copy: PropTypes.string,
  image: imageShape,
  columns: PropTypes.number.isRequired,
  icon: PropTypes.string,
  align: PropTypes.oneOf(['left', 'center', 'right']),
  imageAlign: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  imageWidth: PropTypes.oneOf(['', 25, 33, 50, 75, 100]),
  primaryModule: PropTypes.bool,
  link: PropTypes.object,
  inverted: PropTypes.bool,
  moduleId: PropTypes.string,
  withTextShadows: PropTypes.bool,
};

ContentGroupItem.defaultProps = {};

export default ContentGroupItem;
